import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import { ConfirmDialog, PromptDialog } from '../../components/Dialogs';
import './WarehouseDashboard.css';
import { formatAppDate, localToday } from '../../utils/dateUtils';
import { hasRole } from '../../utils/roleUtils';
import { showApiError } from '../../utils/errorUtils';

// #290: a material return with lines over the issued quantity (EXCESS) or off the requisition (OFF_REQUISITION)
// is received and inspected only by a supervisor (manager, admin); the backend refuses the warehouse.
const FLAGGED_RETURN_ROLES = ['SUPERVISOR', 'MANAGER', 'ADMIN'];
const canHandleFlaggedReturn = (user) => hasRole(user, ...FLAGGED_RETURN_ROLES);  // any of the user's roles (#285)
const SupervisorBadge = ({ ret }) => {
  const { t } = useTranslation('warehouse');
  return ret.requires_supervisor ? (
    <span className="status-badge status-warning" style={{ marginLeft: '0.25rem' }} title={t('returnsAndRequisitions.supervisorOnlyHint')}>
      {t('returnsAndRequisitions.supervisorOnly')}
    </span>
  ) : null;
};

// Flag of one return line, with the operator's reason
const ReturnLineFlag = ({ item }) => {
  const { t } = useTranslation('warehouse');
  return item.return_flag ? (
    <div style={{ marginTop: '0.25rem', fontSize: '0.85em' }}>
      <span className="status-badge status-warning" data-testid={`flag-${item.return_item_id}`}>
        {item.return_flag === 'EXCESS'
          ? t('returnsAndRequisitions.flags.excess', { quantity: parseFloat(item.excess_quantity || 0).toFixed(3), unit: item.unit_of_measure || '' }).trim()
          : t('returnsAndRequisitions.flags.offRequisition')}
      </span>
      {item.flag_reason && <span style={{ marginLeft: '0.25rem', color: 'var(--text-secondary)' }}>{t('returnsAndRequisitions.flags.reason', { reason: item.flag_reason })}</span>}
    </div>
  ) : null;
};

const ReturnsAndRequisitions = forwardRef(({ activeSubTab, initialReturnGRNItem }, ref) => {
  const { t } = useTranslation('warehouse');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useMessages();
  // Backend enum codes shown as labels; unknown codes fall back to the raw code
  const statusLabel = (code) => (code ? t(`returnsAndRequisitions.status.${code}`, { defaultValue: code }) : code);
  const conditionLabel = (code) => (code ? t(`returnsAndRequisitions.conditions.${code}`, { defaultValue: code }) : code);
  const priorityLabel = (code) => (code ? t(`returnsAndRequisitions.priority.${code}`, { defaultValue: code }) : code);

  // Supplier returns states
  const [supplierReturns, setSupplierReturns] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [showReturnDetailsModal, setShowReturnDetailsModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [showCreateReturnModal, setShowCreateReturnModal] = useState(false);
  const [returnGRNItem, setReturnGRNItem] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [returnQuantity, setReturnQuantity] = useState('');
  const [replacementExpected, setReplacementExpected] = useState(false);
  const [returnNotes, setReturnNotes] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);

  // Return workflow action states
  const [showAuthorizeModal, setShowAuthorizeModal] = useState(false);
  const [authorizationNumber, setAuthorizationNumber] = useState('');
  const [authorizationDate, setAuthorizationDate] = useState(localToday());
  const [authorizationNotes, setAuthorizationNotes] = useState('');
  const [submittingAuthorization, setSubmittingAuthorization] = useState(false);

  const [showShipModal, setShowShipModal] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippedDate, setShippedDate] = useState(localToday());
  const [shipNotes, setShipNotes] = useState('');
  const [submittingShip, setSubmittingShip] = useState(false);

  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [creditNoteNumber, setCreditNoteNumber] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [completedDate, setCompletedDate] = useState(localToday());
  const [completeNotes, setCompleteNotes] = useState('');
  const [submittingComplete, setSubmittingComplete] = useState(false);

  // Material issuing states
  const [productionOrders, setProductionOrders] = useState([]);
  const [loadingProductionOrders, setLoadingProductionOrders] = useState(false);
  const [selectedProductionOrder, setSelectedProductionOrder] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [issueQuantity, setIssueQuantity] = useState('');
  const [issueToLocation, setIssueToLocation] = useState('PRODUCTION-FLOOR');
  const [issueNotes, setIssueNotes] = useState('');
  const [submittingIssue, setSubmittingIssue] = useState(false);

  // Material requisitions states
  const [requisitions, setRequisitions] = useState([]);
  const [loadingRequisitions, setLoadingRequisitions] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [requisitionDetails, setRequisitionDetails] = useState(null);
  const [showRequisitionDetailsModal, setShowRequisitionDetailsModal] = useState(false);
  const [loadingRequisitionDetails, setLoadingRequisitionDetails] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [submittingApproval, setSubmittingApproval] = useState(false);

  // Custom dialog states
  const [confirmDialog, setConfirmDialog] = useState({ show: false, title: '', message: '', onConfirm: null });
  const [promptDialog, setPromptDialog] = useState({ show: false, title: '', message: '', fields: [], onSubmit: null });

  // Material returns states
  const [materialReturns, setMaterialReturns] = useState([]);
  const [loadingMaterialReturns, setLoadingMaterialReturns] = useState(false);
  const [selectedMaterialReturn, setSelectedMaterialReturn] = useState(null);
  const [materialReturnDetails, setMaterialReturnDetails] = useState(null);
  const [showMaterialReturnDetailsModal, setShowMaterialReturnDetailsModal] = useState(false);
  const [loadingMaterialReturnDetails, setLoadingMaterialReturnDetails] = useState(false);
  const [showReceiveMaterialReturnModal, setShowReceiveMaterialReturnModal] = useState(false);
  const [receiveReturnNotes, setReceiveReturnNotes] = useState('');
  const [submittingReceive, setSubmittingReceive] = useState(false);
  const [showInspectMaterialReturnModal, setShowInspectMaterialReturnModal] = useState(false);
  const [inspectionCondition, setInspectionCondition] = useState('GOOD');
  const [inspectionNotes, setInspectionNotes] = useState('');
  const [itemInspections, setItemInspections] = useState([]);
  const [submittingInspection, setSubmittingInspection] = useState(false);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [restockNotes, setRestockNotes] = useState('');
  const [submittingRestock, setSubmittingRestock] = useState(false);

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => {
      const refreshMethods = {
        'returns': () => fetchSupplierReturns(),
        'material-returns': () => fetchMaterialReturns(),
        'requisitions': () => fetchRequisitions(),
      };
      const refreshMethod = refreshMethods[activeSubTab];
      if (refreshMethod) {
        refreshMethod();
      }
    }
  }));

  // Fetch data on mount and when activeSubTab changes
  useEffect(() => {
    if (activeSubTab === 'returns') {
      fetchSupplierReturns();
    } else if (activeSubTab === 'material-returns') {
      fetchMaterialReturns();
    } else if (activeSubTab === 'requisitions') {
      fetchRequisitions();
    }
  }, [activeSubTab]);

  // Opened from receiving (Create Return / Return on a GRN line): start the create dialog for that line
  useEffect(() => {
    if (initialReturnGRNItem) handleCreateReturn(initialReturnGRNItem);
  }, [initialReturnGRNItem]);

  // ========================
  // FETCH FUNCTIONS
  // ========================

  const fetchSupplierReturns = async () => {
    try {
      setLoadingReturns(true);
      const response = await apiFetch(getApiUrl('/api/supplier-returns?limit=100'));
      if (response.ok) {
        const data = await response.json();
        setSupplierReturns(data || []);
      }
    } catch (error) {
      console.error('Error fetching supplier returns:', error);
      setSupplierReturns([]);
    } finally {
      setLoadingReturns(false);
    }
  };

  const fetchMaterialReturns = async () => {
    try {
      setLoadingMaterialReturns(true);
      const response = await apiFetch(getApiUrl('/api/material-returns?limit=100'));
      if (response.ok) {
        const data = await response.json();
        setMaterialReturns(data || []);
      }
    } catch (error) {
      console.error('Error fetching material returns:', error);
      setMaterialReturns([]);
    } finally {
      setLoadingMaterialReturns(false);
    }
  };

  const fetchMaterialReturnDetails = async (returnId) => {
    try {
      setLoadingMaterialReturnDetails(true);
      const response = await apiFetch(getApiUrl(`/api/material-returns/${returnId}`));
      if (response.ok) {
        const data = await response.json();
        setMaterialReturnDetails(data);
        // Initialize item inspections for inspection modal
        if (data.items) {
          setItemInspections(data.items.map(item => ({
            return_item_id: item.return_item_id,
            quantity_accepted: item.quantity_returned,
            quantity_rejected: 0,
            item_condition: 'GOOD'
          })));
        }
      }
    } catch (error) {
      console.error('Error fetching material return details:', error);
    } finally {
      setLoadingMaterialReturnDetails(false);
    }
  };

  const fetchProductionOrders = async () => {
    try {
      setLoadingProductionOrders(true);
      const response = await apiFetch(getApiUrl('/api/production/orders?status=IN_PROGRESS&limit=100'));
      if (response.ok) {
        const data = await response.json();
        setProductionOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching production orders:', error);
      setProductionOrders([]);
    } finally {
      setLoadingProductionOrders(false);
    }
  };

  const fetchAllocations = async (productionOrderId) => {
    try {
      setLoadingAllocations(true);
      const response = await apiFetch(getApiUrl(`/api/allocations/by-production-order/${productionOrderId}`));
      if (response.ok) {
        const data = await response.json();
        setAllocations(data || []);
      }
    } catch (error) {
      console.error('Error fetching allocations:', error);
      setAllocations([]);
    } finally {
      setLoadingAllocations(false);
    }
  };

  const fetchRequisitions = async () => {
    try {
      setLoadingRequisitions(true);
      const response = await apiFetch(getApiUrl('/api/inventory-requisitions?limit=100'));
      if (response.ok) {
        const data = await response.json();
        setRequisitions(data || []);
      } else {
        console.error('Failed to fetch requisitions:', response.status, await response.text());
        setRequisitions([]);
      }
    } catch (error) {
      console.error('Error fetching requisitions:', error);
      setRequisitions([]);
    } finally {
      setLoadingRequisitions(false);
    }
  };

  const fetchRequisitionDetails = async (requisitionId) => {
    try {
      setLoadingRequisitionDetails(true);
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/${requisitionId}`));
      if (response.ok) {
        const data = await response.json();
        setRequisitionDetails(data);
      }
    } catch (error) {
      console.error('Error fetching requisition details:', error);
      setRequisitionDetails(null);
    } finally {
      setLoadingRequisitionDetails(false);
    }
  };

  // ========================
  // MATERIAL RETURN HANDLERS
  // ========================

  const handleReceiveMaterialReturn = async (materialReturn) => {
    setSelectedMaterialReturn(materialReturn);
    setReceiveReturnNotes('');
    setShowReceiveMaterialReturnModal(true);
  };

  const handleSubmitReceive = async () => {
    try {
      setSubmittingReceive(true);
      const response = await apiFetch(getApiUrl(`/api/material-returns/${selectedMaterialReturn.return_id}/receive`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          received_by: user.user_id,
          notes: receiveReturnNotes
        })
      });

      if (response.ok) {
        showSuccess(t('success.materialReturns.returnReceived'), t('success.materialReturns.returnReceivedMessage'));
        setShowReceiveMaterialReturnModal(false);
        fetchMaterialReturns();
      } else {
        const error = await response.json();
        showError(t('errors.materialReturns.failedToReceiveReturn'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error receiving material return:', error);
      showError(t('errors.generic.error'), t('errors.materialReturns.failedToReceiveReturnGeneric'));
    } finally {
      setSubmittingReceive(false);
    }
  };

  const handleInspectMaterialReturn = async (materialReturn) => {
    setSelectedMaterialReturn(materialReturn);
    await fetchMaterialReturnDetails(materialReturn.return_id);
    setInspectionCondition('GOOD');
    setInspectionNotes('');
    setShowInspectMaterialReturnModal(true);
  };

  // Returned quantity the inspection neither accepts nor rejects: it did not arrive and does not go back to stock
  const missingQuantity = (returned, accepted, rejected) =>
    Math.max(0, (parseFloat(returned) || 0) - (parseFloat(accepted) || 0) - (parseFloat(rejected) || 0));

  const handleItemInspectionChange = (returnItemId, field, value) => {
    setItemInspections(prev => prev.map(item =>
      item.return_item_id === returnItemId
        ? { ...item, [field]: value }
        : item
    ));
  };

  const handleSubmitInspection = async () => {
    try {
      setSubmittingInspection(true);
      const response = await apiFetch(getApiUrl(`/api/material-returns/${selectedMaterialReturn.return_id}/inspect`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inspected_by: user.user_id,
          condition: inspectionCondition,
          inspection_notes: inspectionNotes,
          item_inspections: itemInspections
        })
      });

      if (response.ok) {
        const result = await response.json();
        const missing = (result.missing_items || []).map(m => `${m.item_code}: ${parseFloat(m.quantity_missing).toFixed(3)}`).join(', ');
        showSuccess(t('success.materialReturns.returnInspected'), t('success.materialReturns.returnInspectedMessage', { status: result.status })
          + (missing ? ` ${t('success.materialReturns.returnInspectedMissing', { items: missing })}` : ''));
        setShowInspectMaterialReturnModal(false);
        setShowMaterialReturnDetailsModal(false);
        fetchMaterialReturns();
      } else {
        const error = await response.json();
        showError(t('errors.materialReturns.failedToInspectReturn'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error inspecting material return:', error);
      showError(t('errors.generic.error'), t('errors.materialReturns.failedToInspectReturnGeneric'));
    } finally {
      setSubmittingInspection(false);
    }
  };

  const handleRestockMaterialReturn = async (materialReturn) => {
    setSelectedMaterialReturn(materialReturn);
    await fetchMaterialReturnDetails(materialReturn.return_id);
    setRestockNotes('');
    setShowRestockModal(true);
  };

  const handleSubmitRestock = async () => {
    try {
      setSubmittingRestock(true);
      const response = await apiFetch(getApiUrl(`/api/material-returns/${selectedMaterialReturn.return_id}/restock`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restocked_by: user.user_id,
          notes: restockNotes
        })
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.materialReturns.materialsRestocked'), t('success.materialReturns.materialsRestockedMessage', { itemsRestocked: result.items_restocked, transactionCount: result.transaction_ids.length }));
        setShowRestockModal(false);
        setShowMaterialReturnDetailsModal(false);
        fetchMaterialReturns();
      } else {
        const error = await response.json();
        showError(t('errors.materialReturns.failedToRestock'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error restocking material return:', error);
      showError(t('errors.generic.error'), t('errors.materialReturns.failedToRestockGeneric'));
    } finally {
      setSubmittingRestock(false);
    }
  };

  const handleViewMaterialReturnDetails = async (materialReturn) => {
    setSelectedMaterialReturn(materialReturn);
    await fetchMaterialReturnDetails(materialReturn.return_id);
    setShowMaterialReturnDetailsModal(true);
  };

  // ========================
  // SUPPLIER RETURN HANDLERS
  // ========================

  const handleViewReturn = (returnItem) => {
    setSelectedReturn(returnItem);
    setShowReturnDetailsModal(true);
  };

  const closeReturnDetailsModal = () => {
    setShowReturnDetailsModal(false);
    setSelectedReturn(null);
  };

  // Return workflow action handlers
  const handleAuthorizeReturn = () => {
    setAuthorizationNumber('');
    setAuthorizationDate(localToday());
    setAuthorizationNotes('');
    setShowAuthorizeModal(true);
  };

  const closeAuthorizeModal = () => {
    setShowAuthorizeModal(false);
    setAuthorizationNumber('');
    setAuthorizationNotes('');
  };

  const submitAuthorization = async () => {
    if (!authorizationNumber || authorizationNumber.trim().length < 3) {
      showWarning(t('common:ui.invalidInput'), t('returnsAndRequisitions.validation.authorizationNumber'));
      return;
    }

    try {
      setSubmittingAuthorization(true);
      const response = await apiFetch(getApiUrl(`/api/supplier-returns/${selectedReturn.return_id}/authorize`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authorization_number: authorizationNumber,
          authorization_date: authorizationDate,
          notes: authorizationNotes || null
        })
      });

      if (response.ok) {
        const updatedReturn = await response.json();
        setSelectedReturn(updatedReturn);
        showSuccess(t('success.materialReturns.returnAuthorized'), t('success.materialReturns.returnAuthorizedMessage'));
        closeAuthorizeModal();
        fetchSupplierReturns(); // Refresh the list
      } else {
        await showApiError(response, showError, tCommon, t('errors.materialReturns.failedToAuthorizeReturn'));
      }
    } catch (error) {
      console.error('Error authorizing return:', error);
      showError(t('errors.generic.error'), t('errors.materialReturns.failedToAuthorizeReturnGeneric'));
    } finally {
      setSubmittingAuthorization(false);
    }
  };

  const handleShipReturn = () => {
    setTrackingNumber('');
    setShippedDate(localToday());
    setShipNotes('');
    setShowShipModal(true);
  };

  const closeShipModal = () => {
    setShowShipModal(false);
    setTrackingNumber('');
    setShipNotes('');
  };

  const submitShipment = async () => {
    if (!trackingNumber || trackingNumber.trim().length < 3) {
      showWarning(t('common:ui.invalidInput'), t('returnsAndRequisitions.validation.trackingNumber'));
      return;
    }

    try {
      setSubmittingShip(true);
      const response = await apiFetch(getApiUrl(`/api/supplier-returns/${selectedReturn.return_id}/ship`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tracking_number: trackingNumber,
          shipped_date: shippedDate,
          notes: shipNotes || null
        })
      });

      if (response.ok) {
        const updatedReturn = await response.json();
        setSelectedReturn(updatedReturn);
        showSuccess(t('success.materialReturns.returnShipped'), updatedReturn.line_quality_status === 'PASSED'
          ? t('success.materialReturns.returnShippedMessage')
          : t('returnsAndRequisitions.toasts.returnShippedFromHeld'));
        closeShipModal();
        fetchSupplierReturns(); // Refresh the list
      } else {
        await showApiError(response, showError, tCommon, t('errors.materialReturns.failedToShipReturn'));
      }
    } catch (error) {
      console.error('Error shipping return:', error);
      showError(t('errors.generic.error'), t('errors.materialReturns.failedToShipReturnGeneric'));
    } finally {
      setSubmittingShip(false);
    }
  };

  const handleCompleteReturn = () => {
    setCreditNoteNumber('');
    setCreditAmount('');
    setCompletedDate(localToday());
    setCompleteNotes('');
    setShowCompleteModal(true);
  };

  const closeCompleteModal = () => {
    setShowCompleteModal(false);
    setCreditNoteNumber('');
    setCreditAmount('');
    setCompleteNotes('');
  };

  const submitCompletion = async () => {
    try {
      setSubmittingComplete(true);
      const response = await apiFetch(getApiUrl(`/api/supplier-returns/${selectedReturn.return_id}/complete`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          completed_date: completedDate,
          credit_note_number: creditNoteNumber || null,
          credit_amount: creditAmount ? parseFloat(creditAmount) : null,
          notes: completeNotes || null
        })
      });

      if (response.ok) {
        const updatedReturn = await response.json();
        setSelectedReturn(updatedReturn);
        showSuccess(t('success.materialReturns.returnCompleted'), t('success.materialReturns.returnCompletedMessage'));
        closeCompleteModal();
        fetchSupplierReturns(); // Refresh the list
      } else {
        await showApiError(response, showError, tCommon, t('errors.materialReturns.failedToCompleteReturn'));
      }
    } catch (error) {
      console.error('Error completing return:', error);
      showError(t('errors.generic.error'), t('errors.materialReturns.failedToCompleteReturnGeneric'));
    } finally {
      setSubmittingComplete(false);
    }
  };

  const handleCancelReturn = () => {
    setConfirmDialog({
      show: true,
      title: t('returnsAndRequisitions.confirm.cancelReturnTitle'),
      message: t('returnsAndRequisitions.confirm.cancelReturnMessage', { number: selectedReturn.return_number }),
      onConfirm: async () => {
        setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
        try {
          const response = await apiFetch(getApiUrl(`/api/supplier-returns/${selectedReturn.return_id}/cancel`), {
            method: 'PUT'
          });

          if (response.ok) {
            const updatedReturn = await response.json();
            setSelectedReturn(updatedReturn);
            showSuccess(t('success.materialReturns.returnCancelled'), t('success.materialReturns.returnCancelledMessage'));
            fetchSupplierReturns(); // Refresh the list
          } else {
            await showApiError(response, showError, tCommon, t('errors.materialReturns.failedToCancelReturn'));
          }
        } catch (error) {
          console.error('Error cancelling return:', error);
          showError(t('errors.generic.error'), t('errors.materialReturns.failedToCancelReturnGeneric'));
        }
      }
    });
  };

  // ========================
  // MATERIAL ISSUING HANDLERS
  // ========================

  const handleSelectPO = async (po) => {
    setSelectedProductionOrder(po);
    await fetchAllocations(po.production_order_id);
  };

  const handleIssueMaterial = (allocation) => {
    setSelectedAllocation(allocation);
    setIssueQuantity(allocation.allocated_quantity);
    setIssueToLocation('PRODUCTION-FLOOR');
    setIssueNotes('');
    setShowIssueModal(true);
  };

  const closeIssueModal = () => {
    setShowIssueModal(false);
    setSelectedAllocation(null);
    setIssueQuantity('');
    setIssueNotes('');
  };

  const submitMaterialIssue = async () => {
    if (!issueQuantity || parseFloat(issueQuantity) <= 0) {
      showWarning(t('common:ui.invalidInput'), t('returnsAndRequisitions.validation.quantity'));
      return;
    }

    try {
      setSubmittingIssue(true);
      const response = await apiFetch(getApiUrl('/api/material-issuing/issue'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allocation_id: selectedAllocation.allocation_id,
          issued_quantity: parseFloat(issueQuantity),
          issued_by: user.user_id,
          to_location: issueToLocation,
          notes: issueNotes || null
        })
      });

      if (response.ok) {
        const result = await response.json();
        closeIssueModal();
        showSuccess(t('success.requisitions.materialIssued'), t('success.requisitions.materialIssuedMessage', { quantityIssued: result.quantity_issued }));
        if (selectedProductionOrder) {
          await fetchAllocations(selectedProductionOrder.production_order_id);
        }
      } else {
        const error = await response.json();
        showError(t('errors.requisitions.failedToIssueMaterial'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error issuing material:', error);
      showError(t('errors.generic.error'), t('errors.requisitions.failedToIssueMaterialGeneric'));
    } finally {
      setSubmittingIssue(false);
    }
  };

  const handleCreateReturn = (grnItem) => {
    setReturnGRNItem(grnItem);
    setReturnQuantity(grnItem.quantity_received.toString());
    // Propose what is left of the line: other returns (not cancelled) already take part of it (#368)
    apiFetch(getApiUrl(`/api/grn-items/${grnItem.grn_item_id}/can-return`))
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data && data.returnable_quantity !== undefined) setReturnQuantity(String(data.returnable_quantity));
      })
      .catch(() => {});
    setReturnReason('');
    setReplacementExpected(false);
    setReturnNotes('');
    setShowCreateReturnModal(true);
  };

  const closeCreateReturnModal = () => {
    setShowCreateReturnModal(false);
    setReturnGRNItem(null);
    setReturnReason('');
    setReturnQuantity('');
    setReplacementExpected(false);
    setReturnNotes('');
  };

  const submitSupplierReturn = async () => {
    if (!returnReason || returnReason.trim().length < 10) {
      showWarning(t('common:ui.invalidInput'), t('returnsAndRequisitions.validation.returnReason'));
      return;
    }

    if (!returnQuantity || parseFloat(returnQuantity) <= 0) {
      showWarning(t('common:ui.invalidInput'), t('returnsAndRequisitions.validation.returnQuantity'));
      return;
    }

    try {
      setSubmittingReturn(true);
      const response = await apiFetch(getApiUrl('/api/supplier-returns'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grn_item_id: returnGRNItem.grn_item_id,
          quantity_returned: parseFloat(returnQuantity),
          return_reason: returnReason,
          replacement_expected: replacementExpected,
          notes: returnNotes || null,
          created_by: user.user_id
        })
      });

      if (response.ok) {
        showSuccess(t('success.supplierReturns.returnCreated'), t('success.supplierReturns.returnCreatedMessage'));
        closeCreateReturnModal();
        fetchSupplierReturns(); // Refresh the returns list
      } else {
        await showApiError(response, showError, tCommon, t('errors.supplierReturns.failedToCreateReturn'));
      }
    } catch (error) {
      console.error('Error creating supplier return:', error);
      showError(t('errors.generic.error'), t('errors.supplierReturns.failedToCreateReturnGeneric'));
    } finally {
      setSubmittingReturn(false);
    }
  };

  // ========================
  // REQUISITION HANDLERS
  // ========================

  const handleViewRequisition = async (requisition) => {
    setSelectedRequisition(requisition);
    setShowRequisitionDetailsModal(true);
    await fetchRequisitionDetails(requisition.requisition_id);
  };

  const closeRequisitionDetailsModal = () => {
    setShowRequisitionDetailsModal(false);
    setSelectedRequisition(null);
    setRequisitionDetails(null);
  };

  const handleApproveRequisition = () => {
    setShowApproveModal(true);
  };

  const closeApproveModal = () => {
    setShowApproveModal(false);
  };

  const submitApproveRequisition = async () => {
    try {
      setSubmittingApproval(true);
      const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/${selectedRequisition.requisition_id}/approve`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved_by: user.user_id
        })
      });

      if (response.ok) {
        closeApproveModal();
        closeRequisitionDetailsModal();
        showSuccess(t('success.requisitions.requisitionApproved'), t('success.requisitions.requisitionApprovedMessage'));
        await fetchRequisitions();
      } else {
        const error = await response.json();
        showError(t('errors.requisitions.failedToApproveRequisition'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error approving requisition:', error);
      showError(t('errors.generic.error'), t('errors.requisitions.failedToApproveRequisitionGeneric'));
    } finally {
      setSubmittingApproval(false);
    }
  };

  const handleRejectRequisition = () => {
    setPromptDialog({
      show: true,
      title: t('returnsAndRequisitions.reject.title'),
      message: t('returnsAndRequisitions.reject.message'),
      fields: [
        { name: 'reason', label: t('returnsAndRequisitions.reject.reasonLabel'), type: 'textarea', placeholder: t('returnsAndRequisitions.reject.reasonPlaceholder'), required: true }
      ],
      onSubmit: async (reason) => {
        setPromptDialog({ show: false, title: '', message: '', fields: [], onSubmit: null });
        try {
          const response = await apiFetch(getApiUrl(`/api/inventory-requisitions/${selectedRequisition.requisition_id}/reject?rejected_by=${user.user_id}&reason=${encodeURIComponent(reason)}`), {
            method: 'PUT'
          });

          if (response.ok) {
            closeRequisitionDetailsModal();
            showSuccess(t('success.requisitions.requisitionRejected'), t('success.requisitions.requisitionRejectedMessage'));
            await fetchRequisitions();
          } else {
            const error = await response.json();
            showError(t('errors.requisitions.failedToRejectRequisition'), error.detail || error.message || t('errors.generic.unknownError'));
          }
        } catch (error) {
          console.error('Error rejecting requisition:', error);
          showError(t('errors.generic.error'), t('errors.requisitions.failedToRejectRequisitionGeneric'));
        }
      }
    });
  };

  const handleGeneratePickListFromRequisition = async () => {
    if (!selectedRequisition) return;

    try {
      const response = await apiFetch(
        getApiUrl(`/api/pick-lists/generate-from-requisition?requisition_id=${selectedRequisition.requisition_id}&created_by=${user.user_id}`),
        { method: 'POST' }
      );

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.requisitions.pickListGenerated'), t('success.requisitions.pickListGeneratedMessage', { pickListNumber: result.pick_list_number, itemsCount: result.items_count }));
        closeRequisitionDetailsModal();
        await fetchRequisitions();
      } else {
        const error = await response.json();
        showError(t('errors.requisitions.failedToGeneratePickList'), error.detail || error.message || t('errors.generic.unknownError'));
      }
    } catch (error) {
      console.error('Error generating pick list from requisition:', error);
      showError(t('errors.requisitions.failedToGeneratePickList'), t('errors.requisitions.failedToGeneratePickListNetwork'));
    }
  };

  // ========================
  // RENDER FUNCTIONS
  // ========================

  const renderSupplierReturns = () => (
    <div className="warehouse-section">
      {loadingReturns ? (
        <div className="loading-state">{t('returnsAndRequisitions.supplierReturns.loading')}</div>
      ) : (
        <div className="warehouse-table-container">
          <table className="warehouse-table">
            <thead>
              <tr>
                <th>{t('returnsAndRequisitions.columns.returnNumber')}</th>
                <th>{t('returnsAndRequisitions.columns.grnReference')}</th>
                <th>{t('common:ui.material')}</th>
                <th>{t('returnsAndRequisitions.columns.supplier')}</th>
                <th>{t('common:ui.quantity')}</th>
                <th>{t('returnsAndRequisitions.columns.returnDate')}</th>
                <th>{t('common:ui.status')}</th>
                <th>{t('common:ui.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {supplierReturns.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    {t('returnsAndRequisitions.supplierReturns.empty')}
                    <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                      {t('returnsAndRequisitions.supplierReturns.emptyHint')}
                    </p>
                  </td>
                </tr>
              ) : (
                supplierReturns.map(returnItem => {
                  let statusClass = 'status-pending';
                  if (returnItem.status === 'AUTHORIZED') statusClass = 'status-warning';
                  if (returnItem.status === 'SHIPPED') statusClass = 'status-in-transit';
                  if (returnItem.status === 'COMPLETED') statusClass = 'status-verified';
                  if (returnItem.status === 'CANCELLED') statusClass = 'status-error';

                  return (
                    <tr key={returnItem.return_id}>
                      <td><strong>{returnItem.return_number}</strong></td>
                      <td>{returnItem.grn_number}</td>
                      <td>
                        <strong>{returnItem.item_code}</strong><br />
                        <span style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>{returnItem.item_name}</span>
                      </td>
                      <td>{returnItem.supplier_name}</td>
                      <td>{parseFloat(returnItem.quantity_returned).toLocaleString()} {returnItem.unit_of_measure || ''}</td>
                      <td>{formatAppDate(returnItem.return_date)}</td>
                      <td><span className={`status-badge ${statusClass}`}>{statusLabel(returnItem.status)}</span></td>
                      <td>
                        <button className="btn-action btn-primary" onClick={() => handleViewReturn(returnItem)}>
                          📋 {t('returnsAndRequisitions.buttons.details')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderMaterialIssuing = () => (
    <div className="warehouse-section">
      <div className="section-header">
        <h2>🏭 {t('returnsAndRequisitions.issuing.title')}</h2>
        <p className="section-subtitle">{t('returnsAndRequisitions.issuing.subtitle')}</p>
      </div>
      <div className="action-buttons">
        <button className="btn-action btn-success" onClick={fetchProductionOrders}>🔄 {t('common:ui.refresh')}</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
        {/* Production Orders List */}
        <div>
          <h3>{t('returnsAndRequisitions.issuing.productionOrders')}</h3>
          {loadingProductionOrders ? (
            <div className="loading-state">{t('returnsAndRequisitions.issuing.loadingProductionOrders')}</div>
          ) : (
            <div className="warehouse-table-container">
              <table className="warehouse-table">
                <thead>
                  <tr>
                    <th>{t('returnsAndRequisitions.columns.poNumber')}</th>
                    <th>{t('common:ui.status')}</th>
                    <th>{t('common:ui.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {productionOrders.length === 0 ? (
                    <tr>
                      <td colSpan="3" style={{ textAlign: 'center', padding: '1rem' }}>
                        {t('returnsAndRequisitions.issuing.noProductionOrders')}
                      </td>
                    </tr>
                  ) : (
                    productionOrders.map(po => (
                      <tr key={po.production_order_id}
                          style={{ backgroundColor: selectedProductionOrder?.production_order_id === po.production_order_id ? 'var(--bg-secondary)' : 'transparent' }}>
                        <td><strong>{po.po_number}</strong></td>
                        <td><span className="status-badge status-in-progress">{statusLabel(po.status)}</span></td>
                        <td>
                          <button className="btn-action btn-primary" onClick={() => handleSelectPO(po)}>
                            {t('common:ui.view')}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Allocations for Selected PO */}
        <div>
          <h3>{selectedProductionOrder
            ? t('returnsAndRequisitions.issuing.allocationsFor', { number: selectedProductionOrder.po_number })
            : t('returnsAndRequisitions.issuing.allocations')}</h3>
          {loadingAllocations ? (
            <div className="loading-state">{t('returnsAndRequisitions.issuing.loadingAllocations')}</div>
          ) : !selectedProductionOrder ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              {t('returnsAndRequisitions.issuing.selectProductionOrder')}
            </div>
          ) : (
            <div className="warehouse-table-container">
              <table className="warehouse-table">
                <thead>
                  <tr>
                    <th>{t('common:ui.material')}</th>
                    <th>{t('common:ui.quantity')}</th>
                    <th>{t('returnsAndRequisitions.columns.fromLocation')}</th>
                    <th>{t('returnsAndRequisitions.columns.batch')}</th>
                    <th>{t('common:ui.status')}</th>
                    <th>{t('common:ui.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '1rem' }}>
                        {t('returnsAndRequisitions.issuing.noAllocations')}
                      </td>
                    </tr>
                  ) : (
                    allocations.map(allocation => {
                      let statusClass = 'status-pending';
                      if (allocation.allocation_status === 'PICKED') statusClass = 'status-warning';
                      if (allocation.allocation_status === 'ISSUED') statusClass = 'status-success';
                      if (allocation.allocation_status === 'CANCELLED') statusClass = 'status-error';

                      return (
                        <tr key={allocation.allocation_id}>
                          <td>
                            <strong>{allocation.item_code}</strong><br />
                            <span style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>{allocation.item_name}</span>
                          </td>
                          <td>{parseFloat(allocation.allocated_quantity).toLocaleString()}</td>
                          <td>{allocation.allocated_from_location}</td>
                          <td>{allocation.batch_number || '-'}</td>
                          <td><span className={`status-badge ${statusClass}`}>{statusLabel(allocation.allocation_status)}</span></td>
                          <td>
                            {allocation.allocation_status === 'PICKED' && (
                              <button className="btn-action btn-success" onClick={() => handleIssueMaterial(allocation)}>
                                {t('returnsAndRequisitions.buttons.issue')}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderMaterialRequisitions = () => (
    <div className="warehouse-section">
      {loadingRequisitions ? (
        <div className="loading-state">{t('returnsAndRequisitions.requisitions.loading')}</div>
      ) : (
        <div className="warehouse-table-container">
          <table className="warehouse-table">
            <thead>
              <tr>
                <th>{t('returnsAndRequisitions.columns.requisitionNumber')}</th>
                <th>{t('returnsAndRequisitions.columns.productionOrder')}</th>
                <th>{t('returnsAndRequisitions.columns.requestedBy')}</th>
                <th>{t('returnsAndRequisitions.columns.requestedDate')}</th>
                <th>{t('common:ui.priority')}</th>
                <th>{t('common:ui.status')}</th>
                <th>{t('returnsAndRequisitions.columns.approvedBy')}</th>
                <th>{t('common:ui.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {requisitions.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    {t('returnsAndRequisitions.requisitions.empty')}
                    <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                      {t('returnsAndRequisitions.requisitions.emptyHint')}
                    </p>
                  </td>
                </tr>
              ) : (
                requisitions.map(req => {
                  let statusClass = 'status-pending';
                  if (req.status === 'PENDING') statusClass = 'status-warning';
                  if (req.status === 'APPROVED') statusClass = 'status-success';
                  if (req.status === 'PICKED') statusClass = 'status-in-progress';
                  if (req.status === 'ISSUED') statusClass = 'status-verified';
                  if (req.status === 'REJECTED') statusClass = 'status-error';
                  if (req.status === 'CANCELLED') statusClass = 'status-inactive';

                  let priorityClass = 'status-secondary';
                  if (req.priority === 'HIGH') priorityClass = 'status-warning';
                  if (req.priority === 'URGENT') priorityClass = 'status-error';

                  return (
                    <tr key={req.requisition_id}>
                      <td><strong>{req.requisition_number}</strong></td>
                      <td>{req.po_number}</td>
                      <td>{req.requested_by_name || t('returnsAndRequisitions.userFallback', { id: req.requested_by })}</td>
                      <td>{formatAppDate(req.requested_date)}</td>
                      <td><span className={`status-badge ${priorityClass}`}>{priorityLabel(req.priority)}</span></td>
                      <td><span className={`status-badge ${statusClass}`}>{statusLabel(req.status)}</span></td>
                      <td>{req.approved_by_name || '-'}</td>
                      <td>
                        <button className="btn-action btn-primary" onClick={() => handleViewRequisition(req)}>
                          📋 {t('returnsAndRequisitions.buttons.details')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderMaterialReturns = () => {
    const pendingReturns = materialReturns.filter(r => r.status === 'PENDING');
    const receivedReturns = materialReturns.filter(r => r.status === 'RECEIVED');
    const inspectedReturns = materialReturns.filter(r => r.status === 'INSPECTED' || r.status === 'ACCEPTED');
    const completedReturns = materialReturns.filter(r => r.status === 'RESTOCKED' || r.status === 'REJECTED');

    return (
      <div className="warehouse-section">

        {loadingMaterialReturns ? (
          <div className="loading-state">{t('returnsAndRequisitions.materialReturns.loading')}</div>
        ) : (
          <>
            {/* Pending Returns - Need to receive */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem', color: 'var(--warning)' }}>⏳ {t('returnsAndRequisitions.materialReturns.pendingCollection', { count: pendingReturns.length })}</h3>
              <div className="warehouse-table-container">
                <table className="warehouse-table">
                  <thead>
                    <tr>
                      <th>{t('returnsAndRequisitions.columns.returnNumber')}</th>
                      <th>{t('returnsAndRequisitions.columns.fromTask')}</th>
                      <th>{t('returnsAndRequisitions.columns.returnedBy')}</th>
                      <th>{t('returnsAndRequisitions.columns.returnDate')}</th>
                      <th>{t('returnsAndRequisitions.columns.items')}</th>
                      <th>{t('returnsAndRequisitions.columns.reason')}</th>
                      <th>{t('common:ui.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingReturns.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                          {t('returnsAndRequisitions.materialReturns.noPending')}
                        </td>
                      </tr>
                    ) : (
                      pendingReturns.map(ret => (
                        <tr key={ret.return_id}>
                          <td><strong>{ret.return_number}</strong><SupervisorBadge ret={ret} /></td>
                          <td>{ret.task_code}</td>
                          <td>{ret.returned_by_name}</td>
                          <td>{formatAppDate(ret.return_date, 'datetime')}</td>
                          <td>{ret.items_count ?? ret.items?.length ?? 0}</td>
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ret.return_reason || '-'}
                          </td>
                          <td>
                            <button className="btn-action btn-primary" onClick={() => handleReceiveMaterialReturn(ret)}
                              disabled={ret.requires_supervisor && !canHandleFlaggedReturn(user)}
                              title={ret.requires_supervisor ? t('returnsAndRequisitions.supervisorOnlyHint') : undefined}>
                              ✅ {t('returnsAndRequisitions.buttons.receive')}
                            </button>
                            <button className="btn-action btn-secondary" onClick={() => handleViewMaterialReturnDetails(ret)}>
                              📋 {t('common:ui.view')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Received Returns - Need to inspect */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem', color: 'var(--info)' }}>🔍 {t('returnsAndRequisitions.materialReturns.awaitingInspection', { count: receivedReturns.length })}</h3>
              <div className="warehouse-table-container">
                <table className="warehouse-table">
                  <thead>
                    <tr>
                      <th>{t('returnsAndRequisitions.columns.returnNumber')}</th>
                      <th>{t('returnsAndRequisitions.columns.fromTask')}</th>
                      <th>{t('returnsAndRequisitions.columns.receivedBy')}</th>
                      <th>{t('returnsAndRequisitions.columns.receivedDate')}</th>
                      <th>{t('returnsAndRequisitions.columns.items')}</th>
                      <th>{t('common:ui.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivedReturns.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                          {t('returnsAndRequisitions.materialReturns.noAwaitingInspection')}
                        </td>
                      </tr>
                    ) : (
                      receivedReturns.map(ret => (
                        <tr key={ret.return_id}>
                          <td><strong>{ret.return_number}</strong><SupervisorBadge ret={ret} /></td>
                          <td>{ret.task_code}</td>
                          <td>{ret.received_by_name}</td>
                          <td>{formatAppDate(ret.received_date, 'datetime')}</td>
                          <td>{ret.items_count ?? ret.items?.length ?? 0}</td>
                          <td>
                            <button className="btn-action btn-warning" onClick={() => handleInspectMaterialReturn(ret)}
                              disabled={ret.requires_supervisor && !canHandleFlaggedReturn(user)}
                              title={ret.requires_supervisor ? t('returnsAndRequisitions.supervisorOnlyHint') : undefined}>
                              🔍 {t('returnsAndRequisitions.buttons.inspect')}
                            </button>
                            <button className="btn-action btn-secondary" onClick={() => handleViewMaterialReturnDetails(ret)}>
                              📋 {t('common:ui.view')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Inspected Returns - Ready to restock */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem', color: 'var(--success)' }}>✓ {t('returnsAndRequisitions.materialReturns.readyToRestock', { count: inspectedReturns.length })}</h3>
              <div className="warehouse-table-container">
                <table className="warehouse-table">
                  <thead>
                    <tr>
                      <th>{t('returnsAndRequisitions.columns.returnNumber')}</th>
                      <th>{t('returnsAndRequisitions.columns.fromTask')}</th>
                      <th>{t('returnsAndRequisitions.columns.inspectedBy')}</th>
                      <th>{t('returnsAndRequisitions.columns.condition')}</th>
                      <th>{t('common:ui.status')}</th>
                      <th>{t('common:ui.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inspectedReturns.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                          {t('returnsAndRequisitions.materialReturns.noReadyToRestock')}
                        </td>
                      </tr>
                    ) : (
                      inspectedReturns.map(ret => (
                        <tr key={ret.return_id}>
                          <td><strong>{ret.return_number}</strong></td>
                          <td>{ret.task_code}</td>
                          <td>{ret.inspected_by_name}</td>
                          <td>
                            <span className={`status-badge ${
                              ret.condition === 'EXCELLENT' || ret.condition === 'GOOD' ? 'status-success' :
                              ret.condition === 'FAIR' ? 'status-warning' : 'status-error'
                            }`}>
                              {conditionLabel(ret.condition)}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${ret.status === 'ACCEPTED' ? 'status-success' : 'status-error'}`}>
                              {statusLabel(ret.status)}
                            </span>
                            {ret.missing_items_count > 0 && (
                              <span className="status-badge status-warning" style={{ marginLeft: '0.25rem' }}>⚠ {t('returnsAndRequisitions.materialReturns.missingCount', { count: ret.missing_items_count })}</span>
                            )}
                          </td>
                          <td>
                            {ret.status === 'ACCEPTED' && (
                              <button className="btn-action btn-success" onClick={() => handleRestockMaterialReturn(ret)}>
                                📦 {t('returnsAndRequisitions.buttons.restock')}
                              </button>
                            )}
                            <button className="btn-action btn-secondary" onClick={() => handleViewMaterialReturnDetails(ret)}>
                              📋 {t('common:ui.view')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Completed Returns */}
            <div>
              <h3 style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>📋 {t('returnsAndRequisitions.materialReturns.completed', { count: completedReturns.length })}</h3>
              <div className="warehouse-table-container">
                <table className="warehouse-table">
                  <thead>
                    <tr>
                      <th>{t('returnsAndRequisitions.columns.returnNumber')}</th>
                      <th>{t('returnsAndRequisitions.columns.fromTask')}</th>
                      <th>{t('common:ui.status')}</th>
                      <th>{t('returnsAndRequisitions.columns.completedDate')}</th>
                      <th>{t('common:ui.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedReturns.slice(0, 10).map(ret => (
                      <tr key={ret.return_id}>
                        <td><strong>{ret.return_number}</strong></td>
                        <td>{ret.task_code}</td>
                        <td>
                          <span className={`status-badge ${ret.status === 'RESTOCKED' ? 'status-verified' : 'status-error'}`}>
                            {statusLabel(ret.status)}
                          </span>
                          {ret.missing_items_count > 0 && (
                            <span className="status-badge status-warning" style={{ marginLeft: '0.25rem' }}>⚠ {t('returnsAndRequisitions.materialReturns.missingCount', { count: ret.missing_items_count })}</span>
                          )}
                        </td>
                        <td>{formatAppDate(ret.restocked_date || ret.updated_date, 'datetime')}</td>
                        <td>
                          <button className="btn-action btn-secondary" onClick={() => handleViewMaterialReturnDetails(ret)}>
                            📋 {t('common:ui.view')}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {completedReturns.length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                          {t('returnsAndRequisitions.materialReturns.noCompleted')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // ========================
  // RENDER SECTION SWITCH
  // ========================

  const renderSection = () => {
    switch (activeSubTab) {
      case 'returns':
        return renderSupplierReturns();
      case 'material-returns':
        return renderMaterialReturns();
      case 'requisitions':
        return renderMaterialRequisitions();
      default:
        return renderMaterialRequisitions();
    }
  };

  return (
    <>
      {renderSection()}

      {/* ======================== */}
      {/* MODALS                   */}
      {/* ======================== */}

      {/* Return Details Modal */}
      {showReturnDetailsModal && selectedReturn && (
        <div className="modal-overlay" onClick={closeReturnDetailsModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>↩️ {t('returnsAndRequisitions.titles.supplierReturnDetails')}</h2>
              <button className="modal-close" onClick={closeReturnDetailsModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.fields.returnNumber')}:</span>
                <span className="info-value"><strong>{selectedReturn.return_number}</strong></span>
                <span className="info-label">{t('common:ui.status')}:</span>
                <span className="info-value">
                  <span className={`status-badge ${
                    selectedReturn.status === 'PENDING_AUTHORIZATION' ? 'status-pending' :
                    selectedReturn.status === 'AUTHORIZED' ? 'status-warning' :
                    selectedReturn.status === 'SHIPPED' ? 'status-in-transit' :
                    selectedReturn.status === 'COMPLETED' ? 'status-verified' :
                    'status-error'
                  }`}>
                    {statusLabel(selectedReturn.status)}
                  </span>
                </span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.columns.grnReference')}:</span>
                <span className="info-value"><strong>{selectedReturn.grn_number}</strong></span>
                <span className="info-label">{t('returnsAndRequisitions.columns.poNumber')}:</span>
                <span className="info-value">{selectedReturn.po_number}</span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('common:ui.material')}:</span>
                <span className="info-value">
                  <strong>{selectedReturn.item_code}</strong> - {selectedReturn.item_name}
                </span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.columns.supplier')}:</span>
                <span className="info-value">{selectedReturn.supplier_name}</span>
                <span className="info-label">{t('common:ui.quantity')}:</span>
                <span className="info-value">
                  {parseFloat(selectedReturn.quantity_returned).toLocaleString()} {selectedReturn.unit_of_measure || ''}
                </span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.columns.returnDate')}:</span>
                <span className="info-value">{formatAppDate(selectedReturn.return_date)}</span>
                <span className="info-label">{t('returnsAndRequisitions.fields.createdBy')}:</span>
                <span className="info-value">{selectedReturn.created_by_name || t('returnsAndRequisitions.userFallbackNumber', { id: selectedReturn.created_by })}</span>
              </div>
              <div className="material-info-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <span className="info-label">{t('returnsAndRequisitions.fields.returnReason')}:</span>
                <span className="info-value" style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '4px', width: '100%' }}>
                  {selectedReturn.return_reason}
                </span>
              </div>

              {/* Authorization Section */}
              {selectedReturn.authorization_number && (
                <>
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '1rem' }}>
                    <h4 style={{ marginBottom: '0.75rem', color: 'var(--text-primary)' }}>📋 {t('returnsAndRequisitions.titles.authorizationDetails')}</h4>
                  </div>
                  <div className="material-info-row">
                    <span className="info-label">{t('returnsAndRequisitions.fields.authorizationNumber')}:</span>
                    <span className="info-value"><strong>{selectedReturn.authorization_number}</strong></span>
                    <span className="info-label">{t('returnsAndRequisitions.fields.authorizationDate')}:</span>
                    <span className="info-value">
                      {selectedReturn.authorization_date ? formatAppDate(selectedReturn.authorization_date) : '-'}
                    </span>
                  </div>
                </>
              )}

              {/* Shipping Section */}
              {selectedReturn.shipped_date && (
                <>
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '1rem' }}>
                    <h4 style={{ marginBottom: '0.75rem', color: 'var(--text-primary)' }}>📦 {t('returnsAndRequisitions.titles.shippingDetails')}</h4>
                  </div>
                  <div className="material-info-row">
                    <span className="info-label">{t('returnsAndRequisitions.fields.shippedDate')}:</span>
                    <span className="info-value">{formatAppDate(selectedReturn.shipped_date)}</span>
                    <span className="info-label">{t('returnsAndRequisitions.fields.trackingNumber')}:</span>
                    <span className="info-value"><strong>{selectedReturn.tracking_number || '-'}</strong></span>
                  </div>
                </>
              )}

              {/* Completion Section */}
              {selectedReturn.completed_date && (
                <>
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: '1rem' }}>
                    <h4 style={{ marginBottom: '0.75rem', color: 'var(--text-primary)' }}>✅ {t('returnsAndRequisitions.titles.completionDetails')}</h4>
                  </div>
                  <div className="material-info-row">
                    <span className="info-label">{t('returnsAndRequisitions.columns.completedDate')}:</span>
                    <span className="info-value">{formatAppDate(selectedReturn.completed_date)}</span>
                    <span className="info-label">{t('returnsAndRequisitions.fields.creditNote')}:</span>
                    <span className="info-value">{selectedReturn.credit_note_number || '-'}</span>
                  </div>
                  {selectedReturn.credit_amount && (
                    <div className="material-info-row">
                      <span className="info-label">{t('returnsAndRequisitions.fields.creditAmount')}:</span>
                      <span className="info-value">
                        <strong>${parseFloat(selectedReturn.credit_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Notes Section */}
              {selectedReturn.notes && (
                <div className="material-info-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="info-label">{t('returnsAndRequisitions.fields.additionalNotes')}:</span>
                  <span className="info-value" style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '4px', width: '100%' }}>
                    {selectedReturn.notes}
                  </span>
                </div>
              )}

              {/* Replacement Info */}
              {selectedReturn.replacement_expected && (
                <div className="material-info-row">
                  <span className="info-label">{t('returnsAndRequisitions.fields.replacementExpected')}:</span>
                  <span className="info-value">
                    <span className="status-badge status-warning">{t('common:ui.yes')}</span>
                    {selectedReturn.replacement_grn_id && ` - ${t('returnsAndRequisitions.fields.replacementGrn', { id: selectedReturn.replacement_grn_id })}`}
                  </span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {/* Action buttons based on current status */}
              {selectedReturn.status === 'PENDING_AUTHORIZATION' && (
                <button className="btn-action btn-primary" onClick={handleAuthorizeReturn}>
                  ✓ {t('returnsAndRequisitions.buttons.authorizeReturn')}
                </button>
              )}
              {selectedReturn.status === 'AUTHORIZED' && (
                <button className="btn-action btn-primary" onClick={handleShipReturn}>
                  📦 {t('returnsAndRequisitions.buttons.shipReturn')}
                </button>
              )}
              {selectedReturn.status === 'SHIPPED' && (
                <button className="btn-action btn-success" onClick={handleCompleteReturn}>
                  ✓ {t('returnsAndRequisitions.buttons.markComplete')}
                </button>
              )}
              {selectedReturn.status !== 'COMPLETED' && selectedReturn.status !== 'CANCELLED' && (
                <button className="btn-action btn-danger" onClick={handleCancelReturn} style={{ marginLeft: '0.5rem' }}>
                  ✕ {t('returnsAndRequisitions.buttons.cancelReturn')}
                </button>
              )}
              <button className="btn-action btn-secondary" onClick={closeReturnDetailsModal} style={{ marginLeft: 'auto' }}>
                {t('common:ui.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Return Modal */}
      {showCreateReturnModal && returnGRNItem && (
        <div className="modal-overlay" onClick={closeCreateReturnModal} style={{ zIndex: 1200 }}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>↩️ {t('returnsAndRequisitions.titles.createSupplierReturn')}</h2>
              <button className="modal-close" onClick={closeCreateReturnModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.fields.grnNumber')}:</span>
                <span className="info-value"><strong>{returnGRNItem.grn_number}</strong></span>
                <span className="info-label">{t('returnsAndRequisitions.columns.poNumber')}:</span>
                <span className="info-value">{returnGRNItem.po_number}</span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('common:ui.material')}:</span>
                <span className="info-value">
                  <strong>{returnGRNItem.item_code}</strong> - {returnGRNItem.item_name}
                </span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.columns.supplier')}:</span>
                <span className="info-value">{returnGRNItem.supplier_name || t('common:ui.unknown')}</span>
                <span className="info-label">{t('returnsAndRequisitions.fields.availableQty')}:</span>
                <span className="info-value">
                  {parseFloat(returnGRNItem.quantity_received).toLocaleString()} {returnGRNItem.unit_of_measure}
                </span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.fields.currentStatus')}:</span>
                <span className="info-value">
                  <span className={`status-badge ${
                    returnGRNItem.quality_status === 'FAILED' ? 'status-error' : 'status-warning'
                  }`}>
                    {statusLabel(returnGRNItem.quality_status)}
                  </span>
                </span>
              </div>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.quantityToReturn')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    value={returnQuantity}
                    onChange={(e) => setReturnQuantity(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.returnReason')} <span style={{ color: '#dc3545' }}>*</span>
                    <span style={{ fontSize: '0.85em', fontWeight: 'normal', color: 'var(--text-secondary)' }}> ({t('returnsAndRequisitions.fields.minimum10Chars')})</span>
                  </label>
                  <textarea
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    placeholder={t('placeholders.issueDescription')}
                    rows={5}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={replacementExpected}
                      onChange={(e) => setReplacementExpected(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      {t('returnsAndRequisitions.fields.replacementDeliveryExpected')}
                    </span>
                  </label>
                  <p style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                    {t('returnsAndRequisitions.fields.replacementHint')}
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.additionalNotesOptional')}
                  </label>
                  <textarea
                    value={returnNotes}
                    onChange={(e) => setReturnNotes(e.target.value)}
                    placeholder={t('placeholders.additionalInformation')}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={closeCreateReturnModal} disabled={submittingReturn}>
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-action btn-danger"
                onClick={submitSupplierReturn}
                disabled={submittingReturn || !returnReason || returnReason.length < 10 || !returnQuantity || parseFloat(returnQuantity) <= 0}
              >
                {submittingReturn ? t('returnsAndRequisitions.buttons.creatingReturn') : `↩️ ${t('returnsAndRequisitions.buttons.createReturn')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Authorize Return Modal */}
      {showAuthorizeModal && selectedReturn && (
        <div className="modal-overlay" onClick={closeAuthorizeModal} style={{ zIndex: 1200 }}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>✓ {t('returnsAndRequisitions.titles.authorizeReturn')}</h2>
              <button className="modal-close" onClick={closeAuthorizeModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.fields.returnNumber')}:</span>
                <span className="info-value"><strong>{selectedReturn.return_number}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('common:ui.material')}:</span>
                <span className="info-value">{selectedReturn.item_code} - {selectedReturn.item_name}</span>
              </div>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.rmaAuthorizationNumber')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={authorizationNumber}
                    onChange={(e) => setAuthorizationNumber(e.target.value)}
                    placeholder={t('placeholders.authorizationNumber')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.authorizationDate')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={authorizationDate}
                    onChange={(e) => setAuthorizationDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.notesOptional')}
                  </label>
                  <textarea
                    value={authorizationNotes}
                    onChange={(e) => setAuthorizationNotes(e.target.value)}
                    placeholder={t('placeholders.authorizationNotes')}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={closeAuthorizeModal} disabled={submittingAuthorization}>
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-action btn-primary"
                onClick={submitAuthorization}
                disabled={submittingAuthorization || !authorizationNumber || authorizationNumber.length < 3}
              >
                {submittingAuthorization ? t('returnsAndRequisitions.buttons.authorizing') : `✓ ${t('returnsAndRequisitions.buttons.authorizeReturn')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ship Return Modal */}
      {showShipModal && selectedReturn && (
        <div className="modal-overlay" onClick={closeShipModal} style={{ zIndex: 1200 }}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>📦 {t('returnsAndRequisitions.titles.shipReturn')}</h2>
              <button className="modal-close" onClick={closeShipModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.fields.returnNumber')}:</span>
                <span className="info-value"><strong>{selectedReturn.return_number}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.fields.rmaNumber')}:</span>
                <span className="info-value">{selectedReturn.authorization_number}</span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('common:ui.material')}:</span>
                <span className="info-value">{selectedReturn.item_code} - {selectedReturn.item_name}</span>
              </div>
              <div style={{ padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '4px', marginTop: '0.5rem' }}>
                <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', margin: 0 }}>
                  {selectedReturn.line_quality_status === 'PASSED'
                    ? <>⚠️ {t('returnsAndRequisitions.ship.stockWarning', { quantity: parseFloat(selectedReturn.quantity_returned).toLocaleString(), unit: selectedReturn.unit_of_measure })}</>
                    : <>ℹ️ {t('returnsAndRequisitions.ship.heldInfo', { quantity: parseFloat(selectedReturn.quantity_returned).toLocaleString(), unit: selectedReturn.unit_of_measure })}</>}
                </p>
              </div>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.trackingNumber')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder={t('placeholders.carrierTrackingNumber')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.shippedDate')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={shippedDate}
                    onChange={(e) => setShippedDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.notesOptional')}
                  </label>
                  <textarea
                    value={shipNotes}
                    onChange={(e) => setShipNotes(e.target.value)}
                    placeholder={t('placeholders.carrierInformation')}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={closeShipModal} disabled={submittingShip}>
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-action btn-primary"
                onClick={submitShipment}
                disabled={submittingShip || !trackingNumber || trackingNumber.length < 3}
              >
                {submittingShip ? t('returnsAndRequisitions.buttons.shipping') : `📦 ${t('returnsAndRequisitions.buttons.shipReturn')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Return Modal */}
      {showCompleteModal && selectedReturn && (
        <div className="modal-overlay" onClick={closeCompleteModal} style={{ zIndex: 1200 }}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>✓ {t('returnsAndRequisitions.titles.completeReturn')}</h2>
              <button className="modal-close" onClick={closeCompleteModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.fields.returnNumber')}:</span>
                <span className="info-value"><strong>{selectedReturn.return_number}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.fields.trackingNumber')}:</span>
                <span className="info-value">{selectedReturn.tracking_number}</span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('common:ui.material')}:</span>
                <span className="info-value">{selectedReturn.item_code} - {selectedReturn.item_name}</span>
              </div>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.columns.completedDate')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={completedDate}
                    onChange={(e) => setCompletedDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.creditNoteNumberOptional')}
                  </label>
                  <input
                    type="text"
                    value={creditNoteNumber}
                    onChange={(e) => setCreditNoteNumber(e.target.value)}
                    placeholder={t('placeholders.creditNoteNumber')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.creditAmountOptional')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    placeholder={t('placeholders.creditAmount')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.notesOptional')}
                  </label>
                  <textarea
                    value={completeNotes}
                    onChange={(e) => setCompleteNotes(e.target.value)}
                    placeholder={t('placeholders.creditDetails')}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={closeCompleteModal} disabled={submittingComplete}>
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-action btn-success"
                onClick={submitCompletion}
                disabled={submittingComplete}
              >
                {submittingComplete ? t('returnsAndRequisitions.buttons.completing') : `✓ ${t('returnsAndRequisitions.buttons.markComplete')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue Material Modal */}
      {showIssueModal && selectedAllocation && (
        <div className="modal-overlay" onClick={closeIssueModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>🏭 {t('returnsAndRequisitions.titles.issueMaterial')}</h2>
              <button className="modal-close" onClick={closeIssueModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('common:ui.material')}:</span>
                <span className="info-value"><strong>{selectedAllocation.item_code}</strong> - {selectedAllocation.item_name}</span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.fields.allocatedQuantity')}:</span>
                <span className="info-value"><strong>{parseFloat(selectedAllocation.allocated_quantity).toLocaleString()}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.columns.fromLocation')}:</span>
                <span className="info-value">{selectedAllocation.allocated_from_location}</span>
                <span className="info-label">{t('returnsAndRequisitions.columns.batch')}:</span>
                <span className="info-value">{selectedAllocation.batch_number || '-'}</span>
              </div>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.issueQuantity')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="number"
                    value={issueQuantity}
                    onChange={(e) => setIssueQuantity(e.target.value)}
                    step="0.001"
                    min="0"
                    max={selectedAllocation.allocated_quantity}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.toLocation')} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={issueToLocation}
                    onChange={(e) => setIssueToLocation(e.target.value)}
                    placeholder={t('placeholders.issueLocation')}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {t('returnsAndRequisitions.fields.notesOptional')}
                  </label>
                  <textarea
                    value={issueNotes}
                    onChange={(e) => setIssueNotes(e.target.value)}
                    placeholder={t('placeholders.materialIssueNotes')}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-action btn-secondary"
                onClick={closeIssueModal}
                disabled={submittingIssue}
              >
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-action btn-success"
                onClick={submitMaterialIssue}
                disabled={submittingIssue || !issueQuantity || !issueToLocation}
              >
                {submittingIssue ? t('returnsAndRequisitions.buttons.issuing') : `🏭 ${t('returnsAndRequisitions.buttons.issueMaterial')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Requisition Details Modal */}
      {showRequisitionDetailsModal && selectedRequisition && (
        <div className="modal-overlay" onClick={closeRequisitionDetailsModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px' }}>
            <div className="modal-header">
              <h2>📋 {t('returnsAndRequisitions.titles.requisitionDetails', { number: selectedRequisition.requisition_number })}</h2>
              <button className="modal-close" onClick={closeRequisitionDetailsModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.columns.productionOrder')}:</span>
                <span className="info-value"><strong>{selectedRequisition.po_number}</strong></span>
                <span className="info-label">{t('common:ui.status')}:</span>
                <span className="info-value">
                  <span className={`status-badge ${
                    selectedRequisition.status === 'PENDING' ? 'status-warning' :
                    selectedRequisition.status === 'APPROVED' ? 'status-success' :
                    selectedRequisition.status === 'PICKED' ? 'status-in-progress' :
                    selectedRequisition.status === 'ISSUED' ? 'status-verified' :
                    selectedRequisition.status === 'REJECTED' ? 'status-error' : 'status-inactive'
                  }`}>
                    {statusLabel(selectedRequisition.status)}
                  </span>
                </span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('returnsAndRequisitions.columns.requestedBy')}:</span>
                <span className="info-value">{selectedRequisition.requested_by_name || t('returnsAndRequisitions.userFallback', { id: selectedRequisition.requested_by })}</span>
                <span className="info-label">{t('returnsAndRequisitions.columns.requestedDate')}:</span>
                <span className="info-value">{formatAppDate(selectedRequisition.requested_date, 'datetime')}</span>
              </div>
              {selectedRequisition.approved_by && (
                <div className="material-info-row">
                  <span className="info-label">{t('returnsAndRequisitions.columns.approvedBy')}:</span>
                  <span className="info-value">{selectedRequisition.approved_by_name || t('returnsAndRequisitions.userFallback', { id: selectedRequisition.approved_by })}</span>
                  <span className="info-label">{t('returnsAndRequisitions.fields.approvedDate')}:</span>
                  <span className="info-value">{formatAppDate(selectedRequisition.approved_date, 'datetime')}</span>
                </div>
              )}
              {selectedRequisition.notes && (
                <div className="material-info-row">
                  <span className="info-label">{t('common:ui.notes')}:</span>
                  <span className="info-value">{selectedRequisition.notes}</span>
                </div>
              )}
            </div>

            <div className="modal-body">
              {loadingRequisitionDetails ? (
                <div className="loading-state">{t('returnsAndRequisitions.requisitions.loadingItems')}</div>
              ) : requisitionDetails?.items && requisitionDetails.items.length > 0 ? (
                <div className="transactions-table-container">
                  <table className="warehouse-table">
                    <thead>
                      <tr>
                        <th>{t('common:ui.material')}</th>
                        <th>{t('returnsAndRequisitions.columns.requested')}</th>
                        <th>{t('returnsAndRequisitions.columns.approved')}</th>
                        <th>{t('returnsAndRequisitions.columns.picked')}</th>
                        <th>{t('returnsAndRequisitions.columns.issued')}</th>
                        <th>{t('returnsAndRequisitions.columns.uom')}</th>
                        <th>{t('common:ui.notes')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requisitionDetails.items.map((item, index) => (
                        <tr key={item.requisition_item_id || index}>
                          <td>
                            <strong>{item.item_code}</strong><br />
                            <span style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>{item.item_name}</span>
                          </td>
                          <td><strong>{parseFloat(item.requested_quantity).toLocaleString()}</strong></td>
                          <td>{item.approved_quantity ? parseFloat(item.approved_quantity).toLocaleString() : '-'}</td>
                          <td>{item.picked_quantity ? parseFloat(item.picked_quantity).toLocaleString() : '-'}</td>
                          <td>{item.issued_quantity ? parseFloat(item.issued_quantity).toLocaleString() : '-'}</td>
                          <td>{item.unit_of_measure || '-'}</td>
                          <td>{item.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-data">
                  <p>{t('returnsAndRequisitions.requisitions.noItems')}</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={closeRequisitionDetailsModal}>{t('common:ui.close')}</button>
              {selectedRequisition.status === 'PENDING' && (
                <>
                  <button className="btn-action btn-error" onClick={handleRejectRequisition}>
                    ❌ {t('returnsAndRequisitions.buttons.reject')}
                  </button>
                  <button className="btn-action btn-success" onClick={handleApproveRequisition}>
                    ✅ {t('returnsAndRequisitions.buttons.approve')}
                  </button>
                </>
              )}
              {selectedRequisition.status === 'APPROVED' && (
                <button className="btn-action btn-primary" onClick={handleGeneratePickListFromRequisition}>
                  📝 {t('returnsAndRequisitions.buttons.generatePickList')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approve Requisition Confirmation Modal */}
      {showApproveModal && selectedRequisition && (
        <div className="modal-overlay" onClick={closeApproveModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>✅ {t('returnsAndRequisitions.titles.approveRequisition')}</h2>
              <button className="modal-close" onClick={closeApproveModal}>✕</button>
            </div>

            <div className="modal-body">
              <p style={{ marginBottom: '1rem' }}>
                {t('returnsAndRequisitions.approve.confirm', { number: selectedRequisition.requisition_number })}
              </p>
              <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                {t('returnsAndRequisitions.approve.hint')}
              </p>
            </div>

            <div className="modal-footer">
              <button
                className="btn-action btn-secondary"
                onClick={closeApproveModal}
                disabled={submittingApproval}
              >
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-action btn-success"
                onClick={submitApproveRequisition}
                disabled={submittingApproval}
              >
                {submittingApproval ? t('returnsAndRequisitions.buttons.approving') : `✅ ${t('returnsAndRequisitions.buttons.confirmApproval')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receive Material Return Modal */}
      {showReceiveMaterialReturnModal && selectedMaterialReturn && (
        <div className="modal-overlay" onClick={() => setShowReceiveMaterialReturnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>✅ {t('returnsAndRequisitions.titles.receiveMaterialReturn')}</h2>
              <button className="modal-close" onClick={() => setShowReceiveMaterialReturnModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p><strong>{t('returnsAndRequisitions.columns.returnNumber')}:</strong> {selectedMaterialReturn.return_number}</p>
              <p><strong>{t('returnsAndRequisitions.columns.fromTask')}:</strong> {selectedMaterialReturn.task_code}</p>
              <p><strong>{t('returnsAndRequisitions.columns.returnedBy')}:</strong> {selectedMaterialReturn.returned_by_name}</p>
              <p style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>{t('returnsAndRequisitions.fields.receiveNotesOptional')}:</p>
              <textarea
                value={receiveReturnNotes}
                onChange={(e) => setReceiveReturnNotes(e.target.value)}
                placeholder={t('placeholders.receivingNotes')}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit'
                }}
              />
            </div>
            <div className="modal-footer">
              <button
                className="btn-action btn-secondary"
                onClick={() => setShowReceiveMaterialReturnModal(false)}
                disabled={submittingReceive}
              >
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-action btn-primary"
                onClick={handleSubmitReceive}
                disabled={submittingReceive}
              >
                {submittingReceive ? t('returnsAndRequisitions.buttons.receiving') : t('returnsAndRequisitions.buttons.confirmReceipt')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inspect Material Return Modal */}
      {showInspectMaterialReturnModal && selectedMaterialReturn && materialReturnDetails && (
        <div className="modal-overlay" onClick={() => setShowInspectMaterialReturnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>🔍 {t('returnsAndRequisitions.titles.inspectMaterialReturn')}</h2>
              <button className="modal-close" onClick={() => setShowInspectMaterialReturnModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p><strong>{t('returnsAndRequisitions.columns.returnNumber')}:</strong> {selectedMaterialReturn.return_number}</p>
              <p><strong>{t('returnsAndRequisitions.columns.fromTask')}:</strong> {selectedMaterialReturn.task_code}</p>

              <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                <label><strong>{t('returnsAndRequisitions.fields.overallCondition')}:</strong></label>
                <select
                  value={inspectionCondition}
                  onChange={(e) => setInspectionCondition(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    marginTop: '0.5rem'
                  }}
                >
                  <option value="EXCELLENT">{conditionLabel('EXCELLENT')}</option>
                  <option value="GOOD">{conditionLabel('GOOD')}</option>
                  <option value="FAIR">{conditionLabel('FAIR')}</option>
                  <option value="DAMAGED">{conditionLabel('DAMAGED')}</option>
                  <option value="CONTAMINATED">{conditionLabel('CONTAMINATED')}</option>
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <strong>{t('returnsAndRequisitions.fields.inspectEachItem')}:</strong>
                <table className="warehouse-table" style={{ marginTop: '0.5rem' }}>
                  <thead>
                    <tr>
                      <th>{t('common:ui.material')}</th>
                      <th>{t('returnsAndRequisitions.columns.returnedQty')}</th>
                      <th>{t('returnsAndRequisitions.columns.acceptQty')}</th>
                      <th>{t('returnsAndRequisitions.columns.rejectQty')}</th>
                      <th>{t('returnsAndRequisitions.columns.missing')}</th>
                      <th>{t('returnsAndRequisitions.columns.condition')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialReturnDetails.items?.map((item, idx) => {
                      const inspection = itemInspections.find(i => i.return_item_id === item.return_item_id) || {};
                      const missing = missingQuantity(item.quantity_returned, inspection.quantity_accepted, inspection.quantity_rejected);
                      return (
                        <tr key={item.return_item_id}>
                          <td>{item.item_code} - {item.item_name}<ReturnLineFlag item={item} /></td>
                          <td>{parseFloat(item.quantity_returned).toFixed(3)} {item.unit_of_measure}</td>
                          <td>
                            <input
                              type="number"
                              value={inspection.quantity_accepted || 0}
                              onChange={(e) => handleItemInspectionChange(item.return_item_id, 'quantity_accepted', e.target.value)}
                              step="0.001"
                              min="0"
                              max={item.quantity_returned}
                              style={{ width: '100px', padding: '0.25rem' }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              value={inspection.quantity_rejected || 0}
                              onChange={(e) => handleItemInspectionChange(item.return_item_id, 'quantity_rejected', e.target.value)}
                              step="0.001"
                              min="0"
                              max={item.quantity_returned}
                              style={{ width: '100px', padding: '0.25rem' }}
                            />
                          </td>
                          <td data-testid={`missing-${item.return_item_id}`} style={missing > 0 ? { color: 'var(--warning)', fontWeight: 600 } : undefined}>
                            {missing.toFixed(3)} {item.unit_of_measure}
                          </td>
                          <td>
                            <select
                              value={inspection.item_condition || 'GOOD'}
                              onChange={(e) => handleItemInspectionChange(item.return_item_id, 'item_condition', e.target.value)}
                              style={{ padding: '0.25rem' }}
                            >
                              <option value="EXCELLENT">{conditionLabel('EXCELLENT')}</option>
                              <option value="GOOD">{conditionLabel('GOOD')}</option>
                              <option value="FAIR">{conditionLabel('FAIR')}</option>
                              <option value="DAMAGED">{conditionLabel('DAMAGED')}</option>
                              <option value="CONTAMINATED">{conditionLabel('CONTAMINATED')}</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  {t('returnsAndRequisitions.materialReturns.missingHint')}
                </p>
              </div>

              <div>
                <label><strong>{t('returnsAndRequisitions.fields.inspectionNotes')}:</strong></label>
                <textarea
                  value={inspectionNotes}
                  onChange={(e) => setInspectionNotes(e.target.value)}
                  placeholder={t('placeholders.inspectionNotes')}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontFamily: 'inherit',
                    marginTop: '0.5rem'
                  }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-action btn-secondary"
                onClick={() => setShowInspectMaterialReturnModal(false)}
                disabled={submittingInspection}
              >
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-action btn-primary"
                onClick={handleSubmitInspection}
                disabled={submittingInspection}
              >
                {submittingInspection ? t('returnsAndRequisitions.buttons.inspecting') : t('returnsAndRequisitions.buttons.completeInspection')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restock Material Return Modal */}
      {showRestockModal && selectedMaterialReturn && materialReturnDetails && (
        <div className="modal-overlay" onClick={() => setShowRestockModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>📦 {t('returnsAndRequisitions.titles.restockMaterials')}</h2>
              <button className="modal-close" onClick={() => setShowRestockModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p><strong>{t('returnsAndRequisitions.columns.returnNumber')}:</strong> {selectedMaterialReturn.return_number}</p>
              <p><strong>{t('returnsAndRequisitions.columns.fromTask')}:</strong> {selectedMaterialReturn.task_code}</p>

              <div style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
                <strong>{t('returnsAndRequisitions.fields.materialsToRestock')}:</strong>
                <table className="warehouse-table" style={{ marginTop: '0.5rem' }}>
                  <thead>
                    <tr>
                      <th>{t('common:ui.material')}</th>
                      <th>{t('returnsAndRequisitions.columns.acceptedQty')}</th>
                      <th>{t('returnsAndRequisitions.columns.condition')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialReturnDetails.items?.filter(item => parseFloat(item.quantity_accepted || 0) > 0).map(item => (
                      <tr key={item.return_item_id}>
                        <td>{item.item_code} - {item.item_name}<ReturnLineFlag item={item} /></td>
                        <td>{parseFloat(item.quantity_accepted).toFixed(3)} {item.unit_of_measure}</td>
                        <td>
                          <span className={`status-badge ${
                            item.item_condition === 'EXCELLENT' || item.item_condition === 'GOOD' ? 'status-success' :
                            item.item_condition === 'FAIR' ? 'status-warning' : 'status-error'
                          }`}>
                            {conditionLabel(item.item_condition)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <label><strong>{t('returnsAndRequisitions.fields.restockNotesOptional')}:</strong></label>
                <textarea
                  value={restockNotes}
                  onChange={(e) => setRestockNotes(e.target.value)}
                  placeholder={t('placeholders.restockingNotes')}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontFamily: 'inherit',
                    marginTop: '0.5rem'
                  }}
                />
              </div>

              <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                  {t('returnsAndRequisitions.materialReturns.restockWarning')}
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-action btn-secondary"
                onClick={() => setShowRestockModal(false)}
                disabled={submittingRestock}
              >
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-action btn-success"
                onClick={handleSubmitRestock}
                disabled={submittingRestock}
              >
                {submittingRestock ? t('returnsAndRequisitions.buttons.restocking') : t('returnsAndRequisitions.buttons.confirmRestock')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Material Return Details Modal */}
      {showMaterialReturnDetailsModal && selectedMaterialReturn && materialReturnDetails && (
        <div className="modal-overlay" onClick={() => setShowMaterialReturnDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>📋 {t('returnsAndRequisitions.titles.materialReturnDetails')}</h2>
              <button className="modal-close" onClick={() => setShowMaterialReturnDetailsModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <p><strong>{t('returnsAndRequisitions.columns.returnNumber')}:</strong> {materialReturnDetails.return_number}</p>
                  <p><strong>{t('returnsAndRequisitions.columns.fromTask')}:</strong> {materialReturnDetails.task_code}</p>
                  <p><strong>{t('returnsAndRequisitions.columns.productionOrder')}:</strong> {materialReturnDetails.po_number}</p>
                  <p><strong>{t('returnsAndRequisitions.columns.returnedBy')}:</strong> {materialReturnDetails.returned_by_name}</p>
                  <p><strong>{t('returnsAndRequisitions.columns.returnDate')}:</strong> {formatAppDate(materialReturnDetails.return_date, 'datetime')}</p>
                </div>
                <div>
                  <p><strong>{t('common:ui.status')}:</strong> <span className={`status-badge status-${materialReturnDetails.status.toLowerCase()}`}>{statusLabel(materialReturnDetails.status)}</span></p>
                  {materialReturnDetails.received_by_name && <p><strong>{t('returnsAndRequisitions.columns.receivedBy')}:</strong> {materialReturnDetails.received_by_name}</p>}
                  {materialReturnDetails.inspected_by_name && <p><strong>{t('returnsAndRequisitions.columns.inspectedBy')}:</strong> {materialReturnDetails.inspected_by_name}</p>}
                  {materialReturnDetails.condition && <p><strong>{t('returnsAndRequisitions.columns.condition')}:</strong> {conditionLabel(materialReturnDetails.condition)}</p>}
                </div>
              </div>

              {materialReturnDetails.return_reason && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>{t('returnsAndRequisitions.fields.returnReason')}:</strong>
                  <p style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
                    {materialReturnDetails.return_reason}
                  </p>
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <strong>{t('returnsAndRequisitions.fields.materials')}:</strong>
                <table className="warehouse-table" style={{ marginTop: '0.5rem' }}>
                  <thead>
                    <tr>
                      <th>{t('common:ui.material')}</th>
                      <th>{t('returnsAndRequisitions.columns.returned')}</th>
                      <th>{t('returnsAndRequisitions.columns.accepted')}</th>
                      <th>{t('returnsAndRequisitions.columns.rejected')}</th>
                      <th>{t('returnsAndRequisitions.columns.missing')}</th>
                      <th>{t('returnsAndRequisitions.columns.condition')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialReturnDetails.items?.map(item => (
                      <tr key={item.return_item_id}>
                        <td>{item.item_code} - {item.item_name}<ReturnLineFlag item={item} /></td>
                        <td>{parseFloat(item.quantity_returned).toFixed(3)} {item.unit_of_measure}</td>
                        <td>{item.quantity_accepted != null ? parseFloat(item.quantity_accepted).toFixed(3) : '-'}</td>
                        <td>{item.quantity_rejected != null ? parseFloat(item.quantity_rejected).toFixed(3) : '-'}</td>
                        <td style={parseFloat(item.quantity_missing) > 0 ? { color: 'var(--warning)', fontWeight: 600 } : undefined}>
                          {item.quantity_missing != null ? parseFloat(item.quantity_missing).toFixed(3) : '-'}
                        </td>
                        <td>{conditionLabel(item.item_condition) || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {materialReturnDetails.inspection_notes && (
                <div>
                  <strong>{t('returnsAndRequisitions.fields.inspectionNotes')}:</strong>
                  <p style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
                    {materialReturnDetails.inspection_notes}
                  </p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-action btn-secondary" onClick={() => setShowMaterialReturnDetailsModal(false)}>
                {t('common:ui.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.show && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={() => {
            // Store the callback before clearing state
            const callback = confirmDialog.onConfirm;
            // Close dialog first
            setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
            // Then call the callback after a brief delay to ensure dialog unmounts
            setTimeout(() => {
              if (callback && typeof callback === 'function') {
                callback();
              }
            }, 50);
          }}
          onCancel={() => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null })}
          confirmText={t('common:ui.confirm')}
          cancelText={t('common:ui.cancel')}
          confirmStyle="danger"
        />
      )}

      {/* Prompt Dialog */}
      {promptDialog.show && (
        <PromptDialog
          title={promptDialog.title}
          message={promptDialog.message}
          fields={promptDialog.fields || [{ name: 'value', label: t('returnsAndRequisitions.fields.value'), type: 'text' }]}
          onSubmit={(value) => {
            // Store the callback before clearing state
            const callback = promptDialog.onSubmit;
            // Close dialog first
            setPromptDialog({ show: false, title: '', message: '', fields: [], onSubmit: null });
            // Then call the callback after a brief delay to ensure dialog unmounts
            setTimeout(() => {
              if (callback && typeof callback === 'function') {
                callback(value);
              }
            }, 50);
          }}
          onCancel={() => setPromptDialog({ show: false, title: '', message: '', fields: [], onSubmit: null })}
          submitText={t('returnsAndRequisitions.buttons.submit')}
          cancelText={t('common:ui.cancel')}
        />
      )}
    </>
  );
});

ReturnsAndRequisitions.displayName = 'ReturnsAndRequisitions';

export default ReturnsAndRequisitions;
